import { createReadonlyArrayView } from '@frontend/utils';
import { noop } from '@frontend/utils';
import { falseFn } from '@frontend/utils';

type Listener<T> = (items: T[]) => void;

export class AsyncFifo<T> {
  //private properties
  #buffer: T[] = []; //#buffer is shared by each active asyncIterator
  #_isOpen = true; //optional
  #_wait: Promise<void> | null | undefined; //optional
  #stopWait = noop;
  #pushListeners: Set<Listener<T>> | null | undefined; //optional to save memory if not used

  public get readonlyBuffer(): ReadonlyArray<T> {
    //Note: createReadonlyArrayView() maintains WeakMap cache to
    //lookup the readonlyArrayView of the buffer, so there are no duplicates created
    return createReadonlyArrayView(this.#buffer);
  }

  public push(...items: T[]): void {
    if (this.#_isOpen) {
      this.#buffer.push(...items);
      this.#stopWait();
      if (this.#pushListeners) {
        this.#pushListeners.forEach((listener) => {
          listener(items);
        });
      }
    }
  }

  //registerPushListener()
  //* useful when using readonlyBuffer and not an asyncIterator
  //* when items are pushed, listener will be called with newly pushed items
  //* returns method to unregisterPushListener
  //  => Note this.close() removes all push listeners
  public registerPushListener(listener: Listener<T>): () => boolean {
    if (this.#_isOpen) {
      if (!this.#pushListeners) {
        this.#pushListeners = new Set();
      }
      this.#pushListeners.add(listener);
      return () =>
        !!this.#pushListeners && this.#pushListeners.delete(listener);
    } else {
      return falseFn;
    }
  }

  //closeAndClear()
  //* closes buffer and empties it
  //* useful if you don't want active asyncIterators to yield remaining items when closed
  public closeAndClear(): void {
    this.close();
    this.#buffer.length = 0;
  }

  //close():
  //* closes the asyncFifo buffers: items can not be pushed anymore.
  //* existing iterators may continue to iterate until their buffer is drained
  public close(): this {
    this.#_isOpen = false;
    this.#stopWait();
    if (this.#pushListeners) {
      this.#pushListeners.clear();
      this.#pushListeners = null;
    }
    return this; //Each active asyncIterator will continue to yield until their bufferIndex reaches end of buffer
  }

  public get isClosed(): boolean {
    return !this.#_isOpen;
  }

  get isOpen(): boolean {
    return !!this.#_isOpen;
  }

  async *[Symbol.asyncIterator](): AsyncIterableIterator<T> {
    let bufferIndex = 0;
    //iterate while buffer is open OR there are still items in buffer to yield
    while (this.#_isOpen || bufferIndex < this.#buffer.length) {
      //yield items from front of buffer (FIFO)
      while (bufferIndex < this.#buffer.length) {
        yield this.#buffer[bufferIndex++];
      }

      if (!this.#_isOpen) {
        break;
      }

      //buffer is empty, so wait until item is pushed or this.close() was called
      //eslint-disable-next-line no-await-in-loop
      await this.#wait;
    }
  }

  //private
  get #wait(): Promise<void> {
    //Reuse this.#_wait or create new this.#_wait that is resolved when this.#stopWait() is called.
    return (
      this.#_wait ||
      (this.#_wait = new Promise<void>((resolve) => {
        this.#stopWait = () => {
          resolve();
          this.#stopWait = noop;
          this.#_wait = null;
        };
      }))
    );
  }
}
